// @flow
import type { Action, Target } from '../types';
import { TYPE, DATA_SOURCE_BUFFER_SIZE } from '../constants/targetConstants';
import * as carConstants from '../constants/carConstants';
import * as planeConstants from '../constants/planeConstants';

export type State<T, E> = $ReadOnly<{
    list: $ReadOnlyArray<$PropertyType<T, 'Icao'>>,
    entities: $ReadOnly<{
        [icao: string]: E
    }>,
    status: $ReadOnly<{
        isLoading: boolean,
        error: boolean
    }>
}>;

type Config = $ReadOnly<{|
    type: typeof TYPE,
    getDataSource: (target: Target) => string
|}>;

const initialState = {
    list: [],
    entities: {},
    status: {
        isLoading: false,
        error: false
    }
};

export default function makeTargetReducer({ type, getDataSource }: Config) {
    const constants = type === TYPE.car ? carConstants : planeConstants;
    const otherConstants = type === TYPE.car ? planeConstants : carConstants;

    return function targetReducer(state: State = initialState, action: Action): State {
        switch(action.type) {
            case constants.SET: {
                const id = action.data.Icao;

                let list = state.list;
                if(list.indexOf(id) === -1) {
                    list = [
                        ...list,
                        id
                    ];
                }

                const entity = state.entities[id];
                const dataSource = getDataSource(action.data);
                const dataSourceBuffer = [
                    action.data,
                    ...(entity && entity[dataSource] ? entity[dataSource] : [])
                ];

                if(dataSourceBuffer.length > DATA_SOURCE_BUFFER_SIZE) {
                    dataSourceBuffer.splice(DATA_SOURCE_BUFFER_SIZE);
                }

                return {
                    ...state,
                    list,
                    entities: {
                        ...state.entities,
                        [id]: {
                            ...entity,
                            sessionStart: (entity && entity.sessionStart) || Date.now(),
                            [dataSource]: dataSourceBuffer
                        }
                    }
                };
            }

            case constants.UNSET:
            case otherConstants.SET: {
                const id = action.data.Icao;

                let list = state.list;
                let isChanged = false;
                const index = list.indexOf(id);
                if(index !== -1) {
                    isChanged = true;
                    list = [].concat(list);
                    list.splice(index, 1);
                }

                let entities = state.entities;
                if(state.entities[id] && (action.type === constants.UNSET || index !== -1) ) {
                    isChanged = true;
                    entities = {
                        ...state.entities
                    };
                    delete entities[id];
                }

                if(!isChanged) {
                    return state;
                }

                return {
                    ...state,
                    list,
                    entities
                };
            }

            case constants.BATCH_SET: {
                let list = state.list;
                let entities = state.entities;
                let listChanged = false;

                const newEntities = {};
                for(const id in action.data) {
                    if(Object.prototype.hasOwnProperty.call(action.data, id)) {
                        const targetBuffer = action.data[id];

                        if(list.indexOf(id) === -1) {
                            if(!listChanged) {
                                list = [].concat(list);
                                listChanged = true;
                            }

                            list.push(id);
                        }

                        const entity = state.entities[id];
                        const buffers = {};
                        for(let i = 0, length = targetBuffer.length; i < length; i += 1) {
                            const target = targetBuffer[i];
                            const dataSource = getDataSource(target);

                            if(!buffers[dataSource]) {
                                buffers[dataSource] = entity && entity[dataSource] ? [].concat(entity[dataSource]) : [];
                            }

                            const buffer = buffers[dataSource];
                            buffer.unshift(target);
                        }

                        for(const dataSource in buffers) {
                            if(Object.prototype.hasOwnProperty.call(buffers, dataSource)) {
                                const buffer = buffers[dataSource];
                                if(buffer.length > DATA_SOURCE_BUFFER_SIZE) {
                                    buffer.splice(DATA_SOURCE_BUFFER_SIZE);
                                }
                            }
                        }

                        newEntities[id] = {
                            ...entity,
                            ...buffers,
                            sessionStart: (entity && entity.sessionStart) || Date.now()
                        };
                    }
                }

                entities = {
                    ...entities,
                    ...newEntities
                };

                return {
                    ...state,
                    list,
                    entities
                };
            }

            case constants.END_SESSION: {
                if(!state.entities[action.data]) {
                    return state;
                }

                const entity = {
                    ...state.entities[action.data]
                };

                delete entity.sessionStart;

                return {
                    ...state,
                    entities: {
                        ...state.entities,
                        [action.data]: entity
                    }
                };
            }

            default:
                return state;
        }
    };
}